import SunAccent from "@/components/feature/SunAccent";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { usePublicTestimonials } from "@/hooks/usePublicContent";

export default function TestimonialsSection() {
  const { data } = usePublicTestimonials();
  const testimonials = data.slice(0, 6);

  return (
    <section id="reviews" className="sun-glaze bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Visitor Reviews
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            Trusted by Families, Schools & Travelers
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Real words from real visitors — the families, students, teams and travellers who have
            stepped onto a Rythugaru farm and come back changed.
          </p>
          <Link
            to="/reviews"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Read all visitor reviews
            <i className="ri-arrow-right-line"></i>
          </Link>
        </Reveal>

        <Reveal className="mb-10 md:mb-12">
          <div className="bg-secondary-50 rounded-lg border border-secondary-200 p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-4 flex items-center gap-5">
              <p className="font-heading text-5xl md:text-6xl text-foreground-950 font-semibold leading-none">
                4.9
              </p>
              <div>
                <div className="flex items-center gap-1 text-accent-500">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
                <p className="text-sm text-foreground-600 mt-1.5">Average visitor rating</p>
              </div>
            </div>
            <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-5 md:border-l md:border-secondary-200 md:pl-8">
              <div>
                <p className="font-heading text-2xl text-foreground-950 font-semibold">1,200+</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Happy Visitors</p>
              </div>
              <div>
                <p className="font-heading text-2xl text-foreground-950 font-semibold">96%</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Would Recommend</p>
              </div>
              <div>
                <p className="font-heading text-2xl text-foreground-950 font-semibold">150+</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">School & Corporate Groups</p>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((t, index) => (
            <Reveal key={t.name} delay={(index % 3) * 90}>
              <figure className="h-full bg-background-100 rounded-lg border border-background-200 p-6 flex flex-col">
                <div className="flex items-center gap-1 text-accent-500 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="text-sm md:text-base text-foreground-700 leading-relaxed flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-background-200 flex items-center gap-3">
                  <span className="w-12 h-12 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={t.avatar}
                      width={200}
                      height={200}
                      alt={`Portrait of ${t.name}, a Rythugaru visitor from ${t.location}`}
                      title={`${t.name} - Rythugaru visitor review`}
                      className="w-full h-full object-cover object-top"
                      decoding="async"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-foreground-950">{t.name}</span>
                    <span className="block text-xs text-foreground-600 mt-0.5 truncate">
                      {t.role} · {t.location}
                    </span>
                  </span>
                </figcaption>
                {t.experience && t.experienceSlug ? (
                  <Link
                    to={`/experiences/${t.experienceSlug}`}
                    className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-plant-line"></i>
                    {t.experience}
                  </Link>
                ) : null}
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}